import { getClientTabId } from '@/lib/observability/client/session'
import { getClientSessionLogger } from '@/lib/observability/logger'
import {
  BOTID_SCRIPT_PATH_PREFIX,
  describeBotIdScriptFailure,
  isBotIdScriptLoadError
} from './botid-script-error'

export { BOTID_SCRIPT_PATH_PREFIX, describeBotIdScriptFailure, isBotIdScriptLoadError }

const NATIVE_FETCH = Symbol.for('morpho.vaults.nativeFetch')
const FAILURE_LOG_STATE = Symbol.for('morpho.vaults.botidFailureLogState')
const FAILURE_LOG_INTERVAL_MS = 60_000

type FailureLogState = {
  lastLoggedAt: number | null
  suppressedCount: number
  bypassUntil: number
}

function getFailureLogState(): FailureLogState {
  const existing: unknown = Reflect.get(globalThis, FAILURE_LOG_STATE)
  if (
    existing &&
    typeof existing === 'object' &&
    'lastLoggedAt' in existing &&
    'suppressedCount' in existing &&
    'bypassUntil' in existing
  ) {
    return existing as FailureLogState
  }

  const state: FailureLogState = { lastLoggedAt: null, suppressedCount: 0, bypassUntil: 0 }
  Reflect.set(globalThis, FAILURE_LOG_STATE, state)
  return state
}

export function captureNativeFetch(): void {
  if (getNativeFetch()) return
  Reflect.set(globalThis, NATIVE_FETCH, window.fetch.bind(window))
}

export function getNativeFetch(): typeof fetch | undefined {
  const nativeFetch: unknown = Reflect.get(globalThis, NATIVE_FETCH)
  return typeof nativeFetch === 'function' ? (nativeFetch as typeof fetch) : undefined
}

export function reportBotIdScriptFailure(event: Event, { fallback }: { fallback: boolean }): void {
  const { script, status } = describeBotIdScriptFailure(event)
  const tabId = getClientTabId()
  const state = getFailureLogState()
  const now = Date.now()

  if (state.lastLoggedAt !== null && now - state.lastLoggedAt < FAILURE_LOG_INTERVAL_MS) {
    state.suppressedCount += 1
    return
  }

  const suppressedCount = state.suppressedCount
  state.lastLoggedAt = now
  state.suppressedCount = 0
  getClientSessionLogger().warn('botid_script_failed', {
    tags: {
      script,
      status: status === null ? 'unknown' : String(status),
      fallback: String(fallback)
    },
    extra: {
      ...(tabId ? { tab_id: tabId } : {}),
      suppressed_count: suppressedCount
    }
  })
}

export const botIdFallbackFetch: typeof fetch = async (input, init) => {
  const state = getFailureLogState()
  const nativeFetch = getNativeFetch()
  if (nativeFetch && Date.now() < state.bypassUntil) {
    const response = await nativeFetch(input, init)
    if (response.headers.get('x-botid-mode') === 'block') state.bypassUntil = 0
    return response
  }

  try {
    return await fetch(input, init)
  } catch (error) {
    if (!isBotIdScriptLoadError(error)) throw error

    reportBotIdScriptFailure(error, { fallback: Boolean(nativeFetch) })
    if (!nativeFetch) throw error
    const response = await nativeFetch(input, init)
    state.bypassUntil =
      response.headers.get('x-botid-mode') === 'block' ? 0 : Date.now() + FAILURE_LOG_INTERVAL_MS
    return response
  }
}
